import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDocs,
  getDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config';
import { LedgerEntry, ClientLedger, ExpensesLedger } from '../../types';
import { generateLedgerKey } from './keys';
import { getCompanyById } from './companies';
import { getExpensesInvoicesByCompanyId } from './expensesInvoices';
import { getLedgerEntriesByClientId } from './revenuesLedgers';

// Add entry to expenses ledger
export const addExpensesLedgerEntry = async (companyId: string, entry: Omit<LedgerEntry, 'id' | 'createdAt' | 'clientId'>): Promise<void> => {
  try {
    // Get the existing ledger
    const ledger = await getExpensesLedgerByCompanyId(companyId);
    if (!ledger) {
      throw new Error('Expenses ledger not found for company');
    }

    // Create new entry with ID
    const newEntry: LedgerEntry = {
      id: Date.now().toString(),
      clientId: companyId,
      ...entry,
      createdAt: new Date()
    };

    // Add to existing entries and sort by date in ascending order, but keep opening balance at the beginning
    const updatedEntries = [...ledger.entries, newEntry];
    updatedEntries.sort((a, b) => {
      // If one is opening balance, it should come first
      if (a.type === 'adjustment' && a.description === 'Opening Balance') return -1;
      if (b.type === 'adjustment' && b.description === 'Opening Balance') return 1;
      
      // If both are opening balance, maintain original order
      if (a.type === 'adjustment' && b.type === 'adjustment' && 
          a.description === 'Opening Balance' && b.description === 'Opening Balance') {
        return 0;
      }
      
      // Otherwise sort by date
      return a.date.getTime() - b.date.getTime();
    });

    // Recalculate running balance for all entries
    let runningBalance = 0;
    const entriesWithRecalculatedBalance = updatedEntries.map(entry => {
      runningBalance += (entry.debit || 0) - (entry.credit || 0);
      return { ...entry, balance: runningBalance };
    });

    // Update the ledger
    await updateExpensesLedger(ledger.id, entriesWithRecalculatedBalance);
  } catch (error) {
    throw new Error(`Error adding expenses ledger entry: ${error}`);
  }
};

// Delete an expenses ledger entry
export const deleteExpensesLedgerEntry = async (companyId: string, entryId: string): Promise<void> => {
  try {
    const ledger = await getExpensesLedgerByCompanyId(companyId);
    if (!ledger) throw new Error('Expenses ledger not found');
    
    // Filter out the entry to delete
    const updatedEntries = ledger.entries.filter(entry => entry.id !== entryId);
    
    // Update the ledger (balance will be recalculated automatically)
    await updateExpensesLedger(ledger.id, updatedEntries);
  } catch (error) {
    throw new Error(`Error deleting expenses ledger entry: ${error}`);
  }
};

// Get company ledger with calculated balances from approved expenses invoices
export const getCompanyLedger = async (companyId: string): Promise<ClientLedger> => {
  try {
    // First try to get the new ledger system
    const expensesLedger = await getExpensesLedgerByCompanyId(companyId);
    if (expensesLedger) {
      return {
        clientId: expensesLedger.companyId,
        clientName: expensesLedger.companyName,
        entries: expensesLedger.entries,
        currentBalance: expensesLedger.currentBalance
      };
    }

    // Fallback to old system if new ledger doesn't exist
    const [company, approvedInvoices, savedEntries] = await Promise.all([
      getCompanyById(companyId),
      getExpensesInvoicesByCompanyId(companyId),
      getLedgerEntriesByClientId(companyId)
    ]);

    if (!company) {
      throw new Error('Company not found');
    }

    const unified: Array<Omit<LedgerEntry, 'id'>> = [] as any;

    // Add opening balance first (it should always be at the beginning)
    const openingBalance = (company as any).openingBalance || 0;
    if (openingBalance > 0) {
      unified.push({
        clientId: company.id,
        date: new Date('1900-01-01'), // Use a very old date to ensure it always stays at top
        description: 'Opening Balance',
        debit: openingBalance,
        credit: 0,
        balance: openingBalance,
        invoiceId: undefined,
        type: 'adjustment',
        createdBy: 'system',
        createdAt: new Date(company.createdAt)
      } as any);
    }

    approvedInvoices.forEach(invoice => {
      unified.push({
        clientId: invoice.clientId,
        date: invoice.date,
        description: invoice.invoiceDescription || `Invoice #${invoice.invoiceNumber}`,
        debit: invoice.total,
        credit: 0,
        balance: 0,
        invoiceId: invoice.id,
        type: 'invoice',
        createdBy: invoice.createdBy,
        createdAt: invoice.createdAt,
        remarks: 'outstand',
        // Include full invoice details for display
        invoiceNumber: invoice.invoiceNumber,
        awbNumber: invoice.awbNumber,
        blNumber: invoice.blNumber,
        portOfDestination: invoice.portOfDestination
      } as any);
    });

    savedEntries.forEach(entry => {
      unified.push({
        clientId: entry.clientId,
        date: entry.date,
        description: entry.description || 'Payment',
        debit: 0,
        credit: entry.credit,
        balance: 0,
        invoiceId: entry.invoiceId,
        type: entry.type,
        createdBy: entry.createdBy,
        createdAt: entry.createdAt
      } as any);
    });

    // Sort by date ascending, but keep opening balance at the beginning
    unified.sort((a, b) => {
      // If one is opening balance, it should come first
      if (a.type === 'adjustment' && a.description === 'Opening Balance') return -1;
      if (b.type === 'adjustment' && b.description === 'Opening Balance') return 1;
      
      // Sort by date, then by creation time for entries with the same date
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      
      if (dateA !== dateB) {
        return dateA - dateB;
      }
      
      // If dates are the same, sort by creation time
      const createdA = new Date(a.createdAt).getTime();
      const createdB = new Date(b.createdAt).getTime();
      return createdA - createdB;
    });

    let runningBalance = 0;
    const entriesWithBalance = unified.map((e, idx) => {
      runningBalance += (e.debit || 0) - (e.credit || 0);
      return {
        id: `${idx}`,
        ...e,
        balance: runningBalance
      } as LedgerEntry;
    });

    return {
      clientId: company.id,
      clientName: company.name,
      entries: entriesWithBalance,
      currentBalance: runningBalance
    };
  } catch (error) {
    throw new Error(`Error getting company ledger: ${error}`);
  }
};

// Expenses Ledgers Collection
export const createExpensesLedger = async (companyId: string, companyName: string, openingBalance: number = 0): Promise<string> => {
  try {
    const ledgerId = generateLedgerKey(companyName);
    
    // Check if ledger already exists
    const existingLedger = await getExpensesLedger(ledgerId);
    if (existingLedger) {
      // Generate new key if exists
      const newLedgerId = generateLedgerKey(companyName);
      const entries = openingBalance > 0 ? [{
        id: '0',
        clientId: companyId,
        date: new Date(),
        description: 'Opening Balance',
        debit: openingBalance,
        credit: 0,
        balance: openingBalance,
        type: 'adjustment',
        createdBy: 'system',
        createdAt: new Date()
      }] : [];
      
      const docRef = doc(db, 'expensesLedgers', newLedgerId);
      await setDoc(docRef, {
        id: newLedgerId,
        companyId,
        companyName,
        entries,
        currentBalance: openingBalance,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return newLedgerId;
    }
    
    const entries = openingBalance > 0 ? [{
      id: '0',
      clientId: companyId,
      date: new Date(),
      description: 'Opening Balance',
      debit: openingBalance,
      credit: 0,
      balance: openingBalance,
      type: 'adjustment',
      createdBy: 'system',
      createdAt: new Date()
    }] : [];
    
    const docRef = doc(db, 'expensesLedgers', ledgerId);
    await setDoc(docRef, {
      id: ledgerId,
      companyId,
      companyName,
      entries,
      currentBalance: openingBalance,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    return ledgerId;
  } catch (error) {
    throw new Error(`Error creating expenses ledger: ${error}`);
  }
};

export const getExpensesLedger = async (ledgerId: string): Promise<ExpensesLedger | null> => {
  try {
    const docRef = doc(db, 'expensesLedgers', ledgerId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        companyId: data.companyId,
        companyName: data.companyName,
        entries: data.entries?.map((entry: any) => ({
          ...entry,
          date: entry.date?.toDate() || new Date(entry.date),
          createdAt: entry.createdAt?.toDate() || new Date(entry.createdAt)
        })) || [],
        currentBalance: data.currentBalance || 0,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as ExpensesLedger;
    }
    return null;
  } catch (error) {
    throw new Error(`Error getting expenses ledger: ${error}`);
  }
};

export const updateExpensesLedger = async (ledgerId: string, entries: LedgerEntry[]): Promise<void> => {
  try {
    // Recalculate running balance for all entries to ensure accuracy
    let runningBalance = 0;
    const entriesWithRecalculatedBalance = entries.map(entry => {
      runningBalance += (entry.debit || 0) - (entry.credit || 0);
      return { ...entry, balance: runningBalance };
    });
    
    // Calculate current balance (which should equal the last entry's balance)
    const currentBalance = runningBalance;
    
    // Clean entries to remove undefined values
    const cleanEntries = entriesWithRecalculatedBalance.map(entry => {
      const cleanEntry: any = {
        id: entry.id,
        clientId: entry.clientId,
        date: entry.date,
        description: entry.description,
        debit: entry.debit || 0,
        credit: entry.credit || 0,
        balance: entry.balance || 0,
        type: entry.type,
        createdBy: entry.createdBy,
        createdAt: entry.createdAt
      };
      
      // Only add optional fields if they exist and are not undefined
      if (entry.invoiceId !== undefined) cleanEntry.invoiceId = entry.invoiceId;
      if (entry.invoiceNumber !== undefined) cleanEntry.invoiceNumber = entry.invoiceNumber;
      if (entry.invoiceDescription !== undefined) cleanEntry.invoiceDescription = entry.invoiceDescription;
      if (entry.awbNumber !== undefined) cleanEntry.awbNumber = entry.awbNumber;
      if (entry.blNumber !== undefined) cleanEntry.blNumber = entry.blNumber;
      if (entry.containerNumber !== undefined) cleanEntry.containerNumber = entry.containerNumber;
      if (entry.packageCount !== undefined) cleanEntry.packageCount = entry.packageCount;
      if (entry.portOfLoading !== undefined) cleanEntry.portOfLoading = entry.portOfLoading;
      if (entry.portOfDischarge !== undefined) cleanEntry.portOfDischarge = entry.portOfDischarge;
      if (entry.airportOfDeparture !== undefined) cleanEntry.airportOfDeparture = entry.airportOfDeparture;
      if (entry.airportOfDestination !== undefined) cleanEntry.airportOfDestination = entry.airportOfDestination;
      if (entry.shippingMethod !== undefined) cleanEntry.shippingMethod = entry.shippingMethod;
      if (entry.remarks !== undefined) cleanEntry.remarks = entry.remarks;
      
      return cleanEntry;
    });
    
    const docRef = doc(db, 'expensesLedgers', ledgerId);
    await updateDoc(docRef, {
      entries: cleanEntries,
      currentBalance,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    throw new Error(`Error updating expenses ledger: ${error}`);
  }
};

export const getExpensesLedgerByCompanyId = async (companyId: string): Promise<ExpensesLedger | null> => {
  try {
    const q = query(
      collection(db, 'expensesLedgers'),
      where('companyId', '==', companyId)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      companyId: data.companyId,
      companyName: data.companyName,
      entries: data.entries?.map((entry: any) => ({
        ...entry,
        date: entry.date?.toDate() || new Date(entry.date),
        createdAt: entry.createdAt?.toDate() || new Date(entry.createdAt)
      })) || [],
      currentBalance: data.currentBalance || 0,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as ExpensesLedger;
  } catch (error) {
    throw new Error(`Error getting expenses ledger by company ID: ${error}`);
  }
};

// Helper function to rebuild expenses ledger from approved invoices
export const rebuildExpensesLedger = async (companyId: string): Promise<void> => {
  try {
    const [company, approvedInvoices] = await Promise.all([
      getCompanyById(companyId),
      getExpensesInvoicesByCompanyId(companyId)
    ]);

    if (!company) {
      throw new Error('Company not found');
    }

    // Get or create ledger
    let ledger = await getExpensesLedgerByCompanyId(companyId);
    if (!ledger) {
      const openingBalance = (company as any).openingBalance || 0;
      const ledgerId = await createExpensesLedger(companyId, company.name, openingBalance);
      ledger = await getExpensesLedger(ledgerId);
    }

    if (!ledger) {
      throw new Error('Failed to create or retrieve ledger');
    }

    // Preserve existing payment entries from current ledger
    const existingPaymentEntries = ledger.entries.filter(entry => entry.type === 'payment');

    // Build unified entries list with ID preservation
    const unified: Array<{ entry: Omit<LedgerEntry, 'id'>, id?: string }> = [];

    // Add opening balance first (it should always be at the beginning)
    const openingBalance = (company as any).openingBalance || 0;
    if (openingBalance > 0) {
      unified.push({
        entry: {
          clientId: company.id,
          date: new Date('1900-01-01'), // Use a very old date to ensure it always stays at top
          description: 'Opening Balance',
          debit: openingBalance,
          credit: 0,
          balance: openingBalance,
          type: 'adjustment',
          createdBy: 'system',
          createdAt: new Date(company.createdAt)
        } as any
      });
    }

    // Add all approved invoices
    approvedInvoices.forEach(invoice => {
      unified.push({
        entry: {
          clientId: invoice.clientId,
          date: invoice.date,
          description: invoice.invoiceDescription || `Invoice #${invoice.invoiceNumber}`,
          debit: invoice.total,
          credit: 0,
          balance: 0,
          invoiceId: invoice.id,
          type: 'invoice',
          createdBy: invoice.createdBy,
          createdAt: invoice.createdAt,
          // Include full invoice details for display
          invoiceNumber: invoice.invoiceNumber,
          awbNumber: invoice.awbNumber,
          blNumber: invoice.blNumber,
          portOfDestination: invoice.portOfDestination
        } as any
      });
    });

    // Add existing payment entries (preserve them)
    existingPaymentEntries.forEach(entry => {
      unified.push({
        entry: {
          clientId: entry.clientId,
          date: entry.date,
          description: entry.description || 'Payment',
          debit: entry.debit || 0,
          credit: entry.credit || 0,
          balance: 0, // Will be recalculated
          invoiceId: entry.invoiceId,
          type: entry.type,
          createdBy: entry.createdBy,
          createdAt: entry.createdAt
        } as any,
        id: entry.id // Preserve the original ID to maintain edit/delete functionality
      });
    });

    // Sort by date ascending, but keep opening balance at the beginning
    unified.sort((a, b) => {
      // If one is opening balance, it should come first
      if (a.entry.type === 'adjustment' && a.entry.description === 'Opening Balance') return -1;
      if (b.entry.type === 'adjustment' && b.entry.description === 'Opening Balance') return 1;
      
      // Sort by date, then by creation time for entries with the same date
      const dateA = new Date(a.entry.date).getTime();
      const dateB = new Date(b.entry.date).getTime();
      
      if (dateA !== dateB) {
        return dateA - dateB;
      }
      
      // If dates are the same, sort by creation time
      const createdA = new Date(a.entry.createdAt).getTime();
      const createdB = new Date(b.entry.createdAt).getTime();
      return createdA - createdB;
    });

    // Compute running balance
    let runningBalance = 0;
    const entriesWithBalance = unified.map((item, idx) => {
      runningBalance += (item.entry.debit || 0) - (item.entry.credit || 0);
      return {
        id: item.id || `${idx}`, // Preserve original ID if available
        ...item.entry,
        balance: runningBalance
      } as LedgerEntry;
    });

    // Update the ledger
    await updateExpensesLedger(ledger.id, entriesWithBalance);
  } catch (error) {
    throw new Error(`Error rebuilding expenses ledger: ${error}`);
  }
};
